'use client'

import { JSX, useLayoutEffect } from 'react'

/**
 * Copied from clients/marketing-pages/index.html:5, storage key included, so a visitor's saved theme
 * carries over from the legacy site. It sets `data-theme` on <html> because `theme.css`'s dark block
 * and the canvas behind the page both hang off it.
 */
const THEME_SCRIPT =
  '(function(){var t="light";try{if(localStorage.getItem("sonicjobs-theme:v1")==="dark")t="dark"}catch(e){}var r=document.documentElement;r.dataset.theme=t;r.style.background=t==="dark"?"#000":"#fff";r.style.colorScheme=t})();'

/**
 * THEME_SCRIPT again, as a real function. Duplicated rather than eval'd, because eval needs CSP
 * `'unsafe-eval'`, which next.config.mjs is marked to drop; nor generated with `(${fn})()`, because
 * coverage instrumentation rewrites the function body into references that do not exist inline.
 * layout.test.tsx runs both copies through one table (`data-theme`, background, colorScheme, and
 * blocked storage), so neither can drift from the other without a test failing.
 */
function applyTheme(): void {
  let theme = 'light'
  try {
    if (localStorage.getItem('sonicjobs-theme:v1') === 'dark') theme = 'dark'
  } catch {
    // Storage blocked (private mode, sandboxed frame): keep the light default, as the script does.
  }
  const root = document.documentElement
  root.dataset.theme = theme
  root.style.background = theme === 'dark' ? '#000' : '#fff'
  root.style.colorScheme = theme
}

/**
 * The inline script covers a full page load: it runs during parse, before first paint. It does not
 * cover client-side navigation, in either direction, because the root layout owns <html> and is
 * shared with the job board:
 *
 * - arriving: React inserts a client-rendered <script> without executing it, so the theme is applied
 *   here instead — in a layout effect, which still lands before paint;
 * - leaving: nothing else would take the theme back off <html>, and the job board would keep
 *   `color-scheme: dark` (dark native form controls and scrollbars) and a black canvas.
 */
export function MarketingTheme(): JSX.Element {
  useLayoutEffect(() => {
    applyTheme()

    return (): void => {
      const root = document.documentElement
      delete root.dataset.theme
      root.style.background = ''
      root.style.colorScheme = ''
    }
  }, [])

  return <script dangerouslySetInnerHTML={{ __html: THEME_SCRIPT }} />
}
